/**
 * Dual-filter bloom over a mip pyramid we own outright.
 *
 * Level i lives at `buffer >> (i + 1)`, so level 0 is half resolution and level 5 of a
 * 960×540 buffer is 15×8 — a footprint that, tented back up, spreads light across a
 * third of the screen. A two-pass gaussian cannot reach that far at any sane tap count,
 * and reach is the entire effect.
 *
 * The down chain and the up chain are separate calls so halation can read a mid pyramid
 * level after the downsample but before the upsample writes into it.
 */

import * as THREE from 'three';
import { BLOOM_BRIGHT_FRAG, BLOOM_DOWN_FRAG, BLOOM_UP_FRAG } from '../shaders/bloom';
import { FullscreenPass, createTarget } from './FullscreenPass';

export class Bloom {
  /** Mip chain, index 0 = half resolution. */
  private targets: THREE.WebGLRenderTarget[] = [];
  private readonly bright: FullscreenPass;
  private readonly down: FullscreenPass;
  private readonly up: FullscreenPass;
  private readonly type: THREE.TextureDataType;
  private readonly maxLevels: number;

  private width = 1;
  private height = 1;

  /** Levels used this frame. Clamped by the §14.6 rung-8 cap. */
  levels: number;
  /** Per-level upsample weights, normalised against the tightest level. */
  readonly weights: number[];
  radius = 1.0;

  constructor(width: number, height: number, type: THREE.TextureDataType, maxLevels: number) {
    this.type = type;
    this.maxLevels = Math.max(1, maxLevels);
    this.levels = this.maxLevels;
    this.weights = new Array<number>(this.maxLevels).fill(1);

    this.bright = new FullscreenPass(BLOOM_BRIGHT_FRAG, {
      tSource: { value: null },
      uTexel: { value: new THREE.Vector2() },
      uThreshold: { value: 0.32 },
      uKnee: { value: 0.2 },
    });

    this.down = new FullscreenPass(BLOOM_DOWN_FRAG, {
      tSource: { value: null },
      uTexel: { value: new THREE.Vector2() },
    });

    this.up = new FullscreenPass(
      BLOOM_UP_FRAG,
      {
        tSource: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uRadius: { value: 1.0 },
        uWeight: { value: 1.0 },
      },
      'add',
    );

    this.resize(width, height);
  }

  /** The accumulated bloom, at half buffer resolution. */
  get texture(): THREE.Texture {
    return this.targets[0].texture;
  }

  /** A pyramid level, clamped into range. Halation's source. */
  mip(index: number): THREE.WebGLRenderTarget {
    const i = Math.max(0, Math.min(this.levels - 1, index));
    return this.targets[i];
  }

  setThreshold(threshold: number, knee: number): void {
    this.bright.uniforms.uThreshold.value = threshold;
    // A zero knee divides by zero in the prefilter curve.
    this.bright.uniforms.uKnee.value = Math.max(1e-3, knee);
  }

  resize(width: number, height: number): void {
    if (width === this.width && height === this.height && this.targets.length > 0) return;
    this.width = width;
    this.height = height;

    for (const target of this.targets) target.dispose();
    this.targets.length = 0;

    for (let i = 0; i < this.maxLevels; i++) {
      const w = Math.max(1, width >> (i + 1));
      const h = Math.max(1, height >> (i + 1));
      // Blurred data wants linear taps; nearest here reintroduces the blockiness the
      // pyramid exists to remove.
      this.targets.push(createTarget(w, h, { type: this.type, filter: THREE.LinearFilter }));
    }
  }

  /** Bright pass plus the halving chain. Leaves every level holding its own band. */
  renderDown(renderer: THREE.WebGLRenderer, source: THREE.Texture, sourceW: number, sourceH: number): void {
    this.bright.uniforms.tSource.value = source;
    (this.bright.uniforms.uTexel.value as THREE.Vector2).set(1 / sourceW, 1 / sourceH);
    this.bright.render(renderer, this.targets[0]);

    for (let i = 1; i < this.levels; i++) {
      const src = this.targets[i - 1];
      this.down.uniforms.tSource.value = src.texture;
      (this.down.uniforms.uTexel.value as THREE.Vector2).set(1 / src.width, 1 / src.height);
      this.down.render(renderer, this.targets[i]);
    }
  }

  /** Tented accumulate back down to level 0, weighted per level. */
  renderUp(renderer: THREE.WebGLRenderer): void {
    this.up.uniforms.uRadius.value = this.radius;
    for (let i = this.levels - 1; i >= 1; i--) {
      const src = this.targets[i];
      this.up.uniforms.tSource.value = src.texture;
      (this.up.uniforms.uTexel.value as THREE.Vector2).set(1 / src.width, 1 / src.height);
      this.up.uniforms.uWeight.value = this.weights[i] ?? 1;
      this.up.render(renderer, this.targets[i - 1]);
    }
  }

  /**
   * TELESCOPING GAIN NORMALISATION.
   *
   * Each up step adds into a level that already holds its own band, so the gain
   * telescopes: `L0 = B0 + w1(B1 + w2(B2 + …))`. The 13-tap downsample preserves mean
   * brightness, so a *large* bright region fills every level and collects the whole
   * sum-of-coefficients, while a point emitter fills only the tightest levels and
   * collects ≈ 1. Without this a 200-enemy swarm would receive roughly three times the
   * bloom gain of a lone enemy and lift the abyssal background off black — which breaks
   * §11.10's readability argument at exactly the density where it matters most.
   *
   * Returns the composite gain that produces the requested strength independent of area.
   */
  normalisedGain(strength: number): number {
    let telescoped = 1;
    for (let i = this.levels - 1; i >= 1; i--) {
      telescoped = 1 + this.weights[i] * telescoped;
    }
    return strength / Math.max(1, telescoped);
  }

  dispose(): void {
    for (const target of this.targets) target.dispose();
    this.targets.length = 0;
    this.bright.dispose();
    this.down.dispose();
    this.up.dispose();
  }
}
